/**
 * SEO competitor comparison data for /vs/:competitor pages.
 *
 * Each entry powers a hybrid comparison page: a neutral feature table
 * followed by a "Why patients choose GLPFlo" section. Pages are orphan
 * (no nav link) but appear in sitemap.xml and llms.txt.
 *
 * Tone: factual, not disparaging. We position differences, not attacks.
 */

const GLP_FLO = {
  name: "GLPFlo",
  pricing: "Flat monthly subscription, all dose tiers included",
  shipping: "Refrigerated overnight with insulated cold-pack mailer",
  insurance: "Not billed (cash-pay direct-to-patient, no insurance hassle)",
  medications: "Compounded Semaglutide and Tirzepatide (503A pharmacy)",
  consult: "HIPAA-compliant secure messaging with a U.S.-licensed prescriber (asynchronous — no video call required)",
  doseAdjust: "Prescriber-supervised titration included in monthly price",
  messaging: "Unlimited HIPAA-compliant messaging with the care team",
  companion: "Free patient companion app (auto step counter, rewards, shot reminders, body tracking — subscribers unlock food log + AI coach)",
  cancel: "Pause or cancel any time, no fees, no scripts",
  hipaa: "BAA-backed vendor stack, audit-logged PHI access, 15-min session timeout",
};

export const COMPETITORS = {
  trimrx: {
    slug: "trimrx",
    displayName: "TrimRx",
    competitor: {
      name: "TrimRx",
      pricing: "Tiered monthly pricing that scales with dose; promotional first-month discounts",
      shipping: "Standard ambient shipping, cold-pack on request",
      insurance: "Not billed",
      medications: "Compounded Semaglutide and Tirzepatide",
      consult: "Async questionnaire-first model with video upgrade available",
      doseAdjust: "Available, may incur add-on fees at higher dose tiers",
      messaging: "Messaging available, typically business-hours response",
      companion: "Limited in-app tracking",
      cancel: "Cancel any time; some promo plans have minimum commitments",
      hipaa: "HIPAA-compliant",
    },
    why: [
      "**Flat pricing forever.** GLPFlo charges the same monthly amount whether you're on a 0.25 mg starter dose or a 2.4 mg maintenance dose. No upcharges for titrating up.",
      "**Cold-chain shipping is standard, not an add-on.** Every GLPFlo order ships in an insulated mailer with a cold pack — included in the monthly price.",
      "**Async prescriber review on day one.** GLPFlo establishes the prescriber-patient relationship through a HIPAA-compliant secure-message review of the medical intake — no video call required, and prescribers respond in under 24 hours.",
      "**Free Companion App with rewards.** Auto step counter, weekly shot reminders, and body-measurement tracking are free to anyone. Subscribers unlock food logging + AI coaching.",
    ],
    summary:
      "TrimRx is a solid mainstream option. GLPFlo differentiates on pricing transparency (flat-rate at all doses), cold-chain shipping included by default, and a free companion app with step-based rewards (no subscription needed to install and earn points).",
  },
  "dad-club-co": {
    slug: "dad-club-co",
    displayName: "Dad Club Co.",
    competitor: {
      name: "Dad Club Co.",
      pricing: "Subscription with men's-health bundle pricing",
      shipping: "Standard shipping, cold-pack included in higher-tier bundles",
      insurance: "Not billed",
      medications: "Compounded Semaglutide; additional men's-health offerings (testosterone, ED therapy, etc.)",
      consult: "Questionnaire-driven with video for higher-tier programs",
      doseAdjust: "Titration available within program",
      messaging: "In-app messaging during business hours",
      companion: "Bundled with the men's-health portal",
      cancel: "Cancel any time",
      hipaa: "HIPAA-compliant",
    },
    why: [
      "**Open to all adults, not gendered.** GLPFlo serves women, men, and non-binary patients with the same flat pricing — no separate \"women's\" or \"men's\" pricing tier.",
      "**GLP-1-only focus.** Our prescribers specialise in metabolic and weight-management therapy, not a broad bundle of men's-health add-ons. That focus shows up in dose-titration quality and care-team responsiveness.",
      "**Transparent pricing on the public site.** No bundle math — your monthly price is one number on the pricing page.",
      "**HIPAA-grade infrastructure with public audit posture.** GLPFlo publishes its compliance approach and posts BAA-backed vendor information for patients who want to verify.",
    ],
    summary:
      "Dad Club Co. is a strong men's-wellness brand with GLP-1 as one of several offerings. GLPFlo focuses exclusively on physician-supervised GLP-1 therapy with flat pricing for every adult patient.",
  },
  hers: {
    slug: "hers",
    displayName: "Hers (Hims & Hers)",
    competitor: {
      name: "Hers (Hims & Hers Health)",
      pricing: "Subscription plans with optional add-ons (skincare, mental health, hair, etc.)",
      shipping: "Standard shipping; cold-chain on weight-loss orders",
      insurance: "Not billed",
      medications: "Compounded GLP-1s alongside a broad women's-wellness catalog",
      consult: "Async questionnaire model with video upgrade",
      doseAdjust: "Titration supported within the weight-loss program",
      messaging: "In-app messaging; response times vary by plan",
      companion: "Hers app covers multiple wellness verticals",
      cancel: "Cancel any time; some bundle plans renew differently",
      hipaa: "HIPAA-compliant",
    },
    why: [
      "**Single-focus on GLP-1 telehealth.** GLPFlo isn't bundling weight-loss with skincare, hair, or mental-health upsells. One thing, done well.",
      "**Flat monthly price across all dose tiers.** No higher fee when you titrate up. The price on the pricing page is the price you pay long-term.",
      "**Async prescriber messaging on day one.** GLPFlo prescribers review your intake and follow up asynchronously through HIPAA-compliant secure messaging — no scheduling, no video required, decisions typically within 24 hours.",
      "**Transparent shipping & cold-chain.** Every order includes the same insulated mailer regardless of plan — and we publish a peer-reviewed stability study explaining warm-vial safety at /shipping-study.",
    ],
    summary:
      "Hers is a household name in women's wellness with GLP-1 as one of many offerings. GLPFlo concentrates entirely on GLP-1 telehealth — flat pricing, async prescriber messaging, and cold-chain shipping included for everyone.",
  },
};

export const COMPETITOR_SLUGS = Object.keys(COMPETITORS);
export { GLP_FLO };
